import {useMemo} from 'react';

import type {RunGrafanaDashboardQueryQuery} from '../../generated/graphql';
import {useCoreweaveIdByRunNameQuery} from '../../generated/graphql';
import {useQuery} from '../../state/graphql/apolloWithAbort';
import {RunGrafanaSlurmDashboardQuery} from '../../state/issues/runIssuesDashboard.query';
import {useRampFlagCoreweaveIssues} from '../../state/issues/useCoreweaveIssues';
import {propagateErrorsContext} from '../../util/errors';
import {useStatsigGateSlurmDaemon} from '../../util/rampFeatureFlags/rampFeatureFlags';
import type {SlurmJobMetadata} from './SlurmJobDrawer/shared';

interface UseSlurmJobActionsParams {
  entityName: string;
  projectName: string;
  runName: string;
  skip?: boolean;
}

type SlurmJobActionsResult =
  | {
      showSlurmActions: true;
      jobId: string;
      clusterName: string | null;
      orgId: string | null;
      region: string | null;
      grafanaUrl: string | null;
      metadata: SlurmJobMetadata;
      readOnly: boolean;
      loading: false;
      refetch: () => void;
    }
  | {
      showSlurmActions: false;
      jobId: null;
      clusterName: null;
      orgId: null;
      region: null;
      grafanaUrl: null;
      metadata: null;
      readOnly: boolean;
      loading: boolean;
      refetch: () => void;
    };

function parseSlurmMetadata(raw: unknown): SlurmJobMetadata {
  if (typeof raw !== 'string' || raw === '') {
    return {};
  }
  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  const pick = (key: string): string | undefined => {
    const value = parsed[key];
    return typeof value === 'string' && value !== '' ? value : undefined;
  };
  const pickUnixSecondsAsIso = (key: string): string | undefined => {
    const value = pick(key);
    if (value == null) {
      return undefined;
    }
    const seconds = Number(value);
    if (!Number.isFinite(seconds) || seconds <= 0) {
      return undefined;
    }
    return new Date(seconds * 1000).toISOString();
  };
  return {
    name: pick('job_name'),
    partition: pick('job_partition'),
    numNodes: pick('job_num_nodes') ?? pick('nnodes'),
    account: pick('job_account'),
    qos: pick('job_qos'),
    user: pick('job_user'),
    cluster: pick('cluster_name'),
    nodelist: pick('job_nodelist') ?? pick('nodelist'),
    submitDir: pick('submit_dir'),
    cpusPerTask: pick('cpus_per_task'),
    memPerCpu: pick('mem_per_cpu'),
    startTime: pickUnixSecondsAsIso('job_start_time'),
    endTime: pickUnixSecondsAsIso('job_end_time'),
  };
}

export function useSlurmJobActions({
  entityName,
  projectName,
  runName,
  skip = false,
}: UseSlurmJobActionsParams): SlurmJobActionsResult {
  const shouldSkip = skip || !runName;

  // cache-and-network so late-arriving slurm metadata (the SDK writes
  // wandb-metadata.json progressively as the run starts up) repaints without
  // waiting for a hard reload or unrelated cache invalidation. Callers can
  // also invoke refetch() on drawer open to force a fresh read.
  const {
    data,
    loading,
    refetch: refetchQuery,
  } = useCoreweaveIdByRunNameQuery({
    variables: {entityName, projectName, runName},
    skip: shouldSkip,
    fetchPolicy: 'cache-and-network',
  });
  const refetch = () => {
    refetchQuery();
  };

  const wandbOrgId = data?.entity?.organizationId ?? null;
  const isFlagEnabled = useStatsigGateSlurmDaemon(wandbOrgId);

  const canQueryGrafana = useRampFlagCoreweaveIssues();
  const ranOnCoreweave = data?.project?.run?.runInfo?.coreweave != null;
  const {data: grafanaData} = useQuery<RunGrafanaDashboardQueryQuery>(
    RunGrafanaSlurmDashboardQuery,
    {
      variables: {entityName, projectName, runName},
      skip: shouldSkip || !canQueryGrafana || !ranOnCoreweave,
      context: propagateErrorsContext(),
    }
  );

  const readOnly = data?.project?.readOnly ?? true;
  const runInfo = data?.project?.run?.runInfo;
  const jobId = runInfo?.slurm?.jobId ?? null;
  const rawMetadata = runInfo?.slurm?.metadata;
  const metadata = useMemo(
    () => parseSlurmMetadata(rawMetadata),
    [rawMetadata]
  );
  const coreweaveClusterName = runInfo?.coreweave?.clusterName ?? null;
  const coreweaveOrgId = runInfo?.coreweave?.orgId ?? null;
  const clusterName = coreweaveClusterName ?? metadata.cluster ?? null;
  const orgId = coreweaveOrgId;
  const region = runInfo?.coreweave?.region ?? null;
  const grafanaUrl =
    grafanaData?.project?.run?.coreweaveGrafanaDashboard || null;

  // Gates "is a Slurm run on CoreWeave", "the viewer's org has the Slurm daemon
  // feature enabled", and "the backend submitSlurmCommand resolver will accept
  // this run" (it requires coreweave.clusterName + coreweave.orgId — see
  // services/gorilla/api/resolver/mutation_slurm.go). Callers should treat this
  // as a single "show Slurm UI" signal rather than a pure type predicate.
  const showSlurmActions =
    isFlagEnabled &&
    jobId != null &&
    coreweaveClusterName != null &&
    coreweaveClusterName !== '' &&
    coreweaveOrgId != null &&
    coreweaveOrgId !== '';

  if (showSlurmActions && jobId != null) {
    return {
      showSlurmActions: true,
      jobId,
      clusterName,
      orgId,
      region,
      grafanaUrl,
      metadata,
      readOnly,
      loading: false,
      refetch,
    };
  }

  return {
    showSlurmActions: false,
    jobId: null,
    clusterName: null,
    orgId: null,
    region: null,
    grafanaUrl: null,
    metadata: null,
    readOnly,
    loading: loading,
    refetch,
  };
}
